// WYVIO UI 1.0 — Réglages V3 (preview)
// Route : /app/parametres-v3
// Reprend le langage visuel Dashboard V3 validé.
// Réutilise 100 % des endpoints existants (agency, team, auth). Aucun système parallèle.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard,
  FolderKanban,
  CalendarClock,
  MessageCircle,
  FileText,
  Settings,
  HelpCircle,
  ArrowRight,
  ChevronDown,
  Building2,
  Users,
  Bell,
  UserSquare2,
  ShieldCheck,
  Pencil,
  Check,
  X as XIcon,
  Mail,
  LogOut,
  Plus,
  ExternalLink,
} from "lucide-react";

import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppSidebar } from "@/components/AppSidebar";

/* ═════════════════════════ Primitives UI ═══════════════════ */
const Row = ({ label, hint, children, testId }) => (
  <div className="grid grid-cols-1 items-start gap-4 border-b border-black/[0.06] py-6 sm:grid-cols-[280px_1fr] sm:gap-10 sm:py-7" data-testid={testId}>
    <div className="min-w-0">
      <div className="text-[13.5px] font-medium text-black">{label}</div>
      {hint && <div className="mt-1 text-[12.5px] text-black/45">{hint}</div>}
    </div>
    <div className="min-w-0">{children}</div>
  </div>
);

const InlineEdit = ({ value, placeholder, onSave, testId, type = "text" }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(value || ""), [value]);

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="group flex w-full items-center justify-between gap-3 text-left"
        data-testid={testId}
      >
        <span className={value ? "text-[14px] text-black" : "text-[14px] text-black/40"}>
          {value || placeholder || "Non renseigné"}
        </span>
        <Pencil size={13} className="flex-shrink-0 text-black/25 transition-colors group-hover:text-black/60" />
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <input
        type={type}
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={placeholder}
        className="min-w-0 flex-1 rounded-lg bg-white px-3 py-2 text-[14px] text-black placeholder:text-black/30 outline-none transition-shadow"
        style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.02)" }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.currentTarget.blur(); }
          if (e.key === "Escape") { setDraft(value || ""); setEditing(false); }
        }}
      />
      <button
        onClick={async () => {
          if (draft === (value || "")) { setEditing(false); return; }
          setSaving(true);
          try { await onSave(draft); setEditing(false); }
          catch (err) { toast.error(err.message || "Erreur"); }
          finally { setSaving(false); }
        }}
        disabled={saving}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-[#C8FF2F] text-black transition-transform hover:scale-[1.03] disabled:opacity-60"
        data-testid={`${testId}-save`}
      >
        <Check size={14} />
      </button>
      <button
        onClick={() => { setDraft(value || ""); setEditing(false); }}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-black/60"
        style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }}
      >
        <XIcon size={14} />
      </button>
    </div>
  );
};

const Switch = ({ checked, onChange, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    data-testid={testId}
    className={`relative h-6 w-10 flex-shrink-0 rounded-full transition-colors ${checked ? "bg-[#C8FF2F]" : "bg-black/10"}`}
  >
    <span
      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${checked ? "translate-x-[18px]" : "translate-x-0.5"}`}
    />
  </button>
);

/* ═════════════════════════ Sous-navigation ═════════════════ */
const TABS = [
  { key: "agence",       label: "Agence",           icon: Building2 },
  { key: "equipe",       label: "Équipe",           icon: Users },
  { key: "notifications",label: "Notifications",   icon: Bell },
  { key: "client",       label: "Espace client",    icon: UserSquare2 },
  { key: "compte",       label: "Compte & sécurité", icon: ShieldCheck },
];

const SubNav = ({ active, onChange }) => (
  <div className="flex flex-wrap items-center gap-1 border-b border-black/[0.06] pb-2" data-testid="settings-subnav">
    {TABS.map(({ key, label, icon: Icon }) => {
      const on = active === key;
      return (
        <button
          key={key}
          onClick={() => onChange(key)}
          data-testid={`settings-tab-${key}`}
          className={[
            "relative inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[12.5px] font-medium transition-colors",
            on ? "bg-black text-white" : "text-black/60 hover:text-black",
          ].join(" ")}
        >
          <Icon size={13} strokeWidth={1.75} />
          {label}
        </button>
      );
    })}
  </div>
);

/* ═════════════════════════ Section AGENCE ══════════════════ */
const AgencySection = ({ agency, onUpdate }) => {
  const save = (field) => async (val) => {
    const res = await api.updateAgency({ [field]: val });
    onUpdate(res);
    toast.success("Enregistré");
  };
  const saveSetting = (field) => async (val) => {
    const patch = { settings: { ...(agency?.settings || {}), [field]: val } };
    const res = await api.updateAgency(patch);
    onUpdate(res);
    toast.success("Enregistré");
  };

  return (
    <div>
      <Row label="Nom de l'agence" hint="Utilisé dans le portail client et les emails." testId="row-name">
        <InlineEdit value={agency?.name} onSave={save("name")} testId="edit-name" placeholder="SimpliciCar Toulon Est" />
      </Row>
      <Row label="Nom commercial" hint="Nom affiché aux clients (optionnel).">
        <InlineEdit value={agency?.settings?.advisor_name} onSave={saveSetting("advisor_name")} testId="edit-advisor" placeholder="Nom commercial" />
      </Row>
      <Row label="Adresse">
        <InlineEdit value={agency?.address} onSave={save("address")} testId="edit-address" placeholder="123 rue…" />
      </Row>
      <Row label="Téléphone">
        <InlineEdit value={agency?.phone} onSave={save("phone")} testId="edit-phone" placeholder="+33 …" type="tel" />
      </Row>
      <Row label="Email" hint="Email de contact affiché aux clients.">
        <InlineEdit value={agency?.email} onSave={save("email")} testId="edit-email" placeholder="contact@…" type="email" />
      </Row>
      <Row label="Site internet">
        <InlineEdit value={agency?.website} onSave={save("website")} testId="edit-website" placeholder="simplicicar-toulon.fr" />
      </Row>
      <Row label="Responsable">
        <InlineEdit value={agency?.settings?.advisor_name} onSave={saveSetting("advisor_name")} testId="edit-manager" placeholder="Nom du responsable" />
      </Row>
    </div>
  );
};

/* ═════════════════════════ Section ÉQUIPE ══════════════════ */
const ROLE_LABEL = { agency_owner: "Administrateur", manager: "Manager", salesperson: "Commercial", collaborateur: "Commercial", admin: "Administrateur" };
const STATUS_LABEL = { active: "Actif", pending: "Invitation envoyée", inactive: "Désactivé", archived: "Archivé" };
const STATUS_DOT = { active: "#C8FF2F", pending: "#EAB308", inactive: "#94A3B8", archived: "#94A3B8" };

const TeamSection = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invitingOpen, setInvitingOpen] = useState(false);
  const [inv, setInv] = useState({ name: "", email: "", role: "salesperson" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.listAgencyUsers?.() ?? await api.getAgencyUsers?.();
      setMembers(Array.isArray(r) ? r : (r?.users || []));
    } catch (e) { toast.error("Impossible de charger l'équipe"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!inv.name || !inv.email) { toast.error("Nom et email requis"); return; }
    setBusy(true);
    try {
      await api.inviteTeamMember({ name: inv.name, email: inv.email, role: inv.role });
      toast.success("Invitation envoyée");
      setInv({ name: "", email: "", role: "salesperson" });
      setInvitingOpen(false);
      load();
    } catch (e) { toast.error(e.message || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-black/[0.06] pb-5 pt-2">
        <div className="text-[12.5px] text-black/55">
          {loading ? "…" : `${members.length} membre${members.length > 1 ? "s" : ""}`}
        </div>
        <button
          onClick={() => setInvitingOpen((v) => !v)}
          className="inline-flex items-center gap-2 rounded-full bg-[#C8FF2F] px-4 py-2 text-[13px] font-medium text-black transition-transform hover:scale-[1.01]"
          style={{ boxShadow: "0 4px 10px -6px rgba(200,255,47,0.4), 0 1px 2px rgba(0,0,0,0.04)" }}
          data-testid="team-invite-btn"
        >
          <Plus size={14} /> Inviter un membre
        </button>
      </div>

      {invitingOpen && (
        <div className="border-b border-black/[0.06] py-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1.5fr_auto_auto]">
            <input
              placeholder="Nom complet"
              value={inv.name}
              onChange={(e) => setInv({ ...inv, name: e.target.value })}
              className="rounded-lg bg-white px-3 py-2 text-[14px] outline-none"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }}
              data-testid="team-invite-name"
            />
            <input
              placeholder="Email"
              type="email"
              value={inv.email}
              onChange={(e) => setInv({ ...inv, email: e.target.value })}
              className="rounded-lg bg-white px-3 py-2 text-[14px] outline-none"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }}
              data-testid="team-invite-email"
            />
            <select
              value={inv.role}
              onChange={(e) => setInv({ ...inv, role: e.target.value })}
              className="rounded-lg bg-white px-3 py-2 text-[13.5px] outline-none"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }}
              data-testid="team-invite-role"
            >
              <option value="salesperson">Commercial</option>
              <option value="manager">Manager</option>
              <option value="admin">Administrateur</option>
            </select>
            <button
              onClick={invite}
              disabled={busy}
              className="rounded-full bg-black px-4 py-2 text-[13px] font-medium text-white disabled:opacity-60"
              data-testid="team-invite-send"
            >
              {busy ? "Envoi…" : "Envoyer"}
            </button>
          </div>
        </div>
      )}

      {/* Liste */}
      <div>
        {loading && [0, 1, 2].map((i) => (
          <div key={i} className="grid grid-cols-[36px_1fr_120px_120px] items-center gap-4 border-b border-black/[0.06] py-4">
            <div className="h-9 w-9 animate-pulse rounded-full bg-black/[0.06]" />
            <div className="space-y-1.5"><div className="h-3.5 w-40 animate-pulse rounded-full bg-black/[0.05]" /><div className="h-2.5 w-28 animate-pulse rounded-full bg-black/[0.04]" /></div>
            <div className="h-3 w-20 animate-pulse rounded-full bg-black/[0.05]" />
            <div className="h-3 w-16 animate-pulse rounded-full bg-black/[0.05]" />
          </div>
        ))}
        {!loading && members.map((m) => {
          const status = m.status || (m.is_active === false ? "inactive" : "active");
          return (
            <div
              key={m.id}
              className="grid grid-cols-[36px_1fr_140px_140px] items-center gap-4 border-b border-black/[0.06] py-4"
              data-testid={`team-row-${m.id}`}
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-[11px] font-semibold text-white">
                {(m.name || "??").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="truncate text-[14px] font-medium text-black">{m.name}</div>
                <div className="truncate text-[12.5px] text-black/45">{m.email}</div>
              </div>
              <div className="text-[13px] text-black/70">{ROLE_LABEL[m.role] || m.role}</div>
              <div className="flex items-center gap-2 text-[12.5px] text-black/55">
                <span className="h-[6px] w-[6px] rounded-full" style={{ background: STATUS_DOT[status] || "#94A3B8" }} />
                {STATUS_LABEL[status] || status}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ═════════════════════════ Section NOTIFICATIONS ═══════════ */
const NotificationsSection = ({ agency, onUpdate }) => {
  const s = agency?.settings || {};
  const update = async (patch) => {
    const res = await api.updateAgency({ settings: { ...s, ...patch } });
    onUpdate(res);
    toast.success("Enregistré");
  };
  const channels = [
    { key: "notify_email", label: "Email",             hint: "Envoi via Resend — nos emails partent depuis noreply@wyvio.app." },
    { key: "notify_sms",   label: "SMS",               hint: "Envoi via Sinch — configurable dans Communications." },
    { key: "notify_push",  label: "Notifications app", hint: "Affichées dans WYVIO à la connexion." },
  ];
  return (
    <div>
      <div className="border-b border-black/[0.06] pb-5 pt-2 text-[11px] uppercase tracking-[0.16em] text-black/40">
        Canaux
      </div>
      {channels.map((c) => (
        <Row key={c.key} label={c.label} hint={c.hint} testId={`row-${c.key}`}>
          <div className="flex justify-end">
            <Switch
              checked={!!s[c.key]}
              onChange={(v) => update({ [c.key]: v })}
              testId={`switch-${c.key}`}
            />
          </div>
        </Row>
      ))}
      <div className="mt-8 rounded-2xl bg-white/60 p-5 text-[12.5px] text-black/50"
           style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}>
        Les événements suivants suivront les canaux ci-dessus : nouveau message client, document reçu, document manquant,
        paiement à confirmer, livraison prochaine, dossier bloqué.
      </div>
    </div>
  );
};

/* ═════════════════════════ Section ESPACE CLIENT ══════════════ */
const ClientSection = ({ agency, onUpdate }) => {
  const s = agency?.settings || {};
  const saveSetting = (field) => async (val) => {
    const res = await api.updateAgency({ settings: { ...s, [field]: val } });
    onUpdate(res);
    toast.success("Enregistré");
  };
  const preview = () => window.open("/client", "_blank");

  return (
    <div>
      <Row label="Nom affiché au client" hint="Titre de l'entête dans le portail client.">
        <InlineEdit value={s.welcome_title} onSave={saveSetting("welcome_title")} testId="edit-welcome-title" placeholder="Votre espace wyvio" />
      </Row>
      <Row label="Message d'accueil" hint="Affiché sur la page d'accueil du client.">
        <InlineEdit value={s.welcome_message} onSave={saveSetting("welcome_message")} testId="edit-welcome-msg" placeholder="Votre véhicule avance…" />
      </Row>
      <Row label="Message de fin" hint="Affiché quand le dossier est terminé.">
        <InlineEdit value={s.end_message} onSave={saveSetting("end_message")} testId="edit-end-msg" placeholder="Votre dossier est terminé…" />
      </Row>
      <Row label="Coordonnées visibles" hint="Affiche téléphone et email agence dans l'espace client.">
        <div className="flex justify-end"><Switch checked={s.show_contact !== false} onChange={saveSetting("show_contact")} testId="switch-show-contact" /></div>
      </Row>

      <div className="mt-10 flex justify-start">
        <button
          onClick={preview}
          className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[13px] font-medium text-black/75 transition-colors hover:text-black"
          style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.03), inset 0 0 0 1px rgba(0,0,0,0.06)" }}
          data-testid="client-preview-btn"
        >
          Prévisualiser l&apos;espace client <ExternalLink size={13} />
        </button>
      </div>
    </div>
  );
};

/* ═════════════════════════ Section COMPTE & SÉCURITÉ ═══════ */
const AccountSection = ({ user }) => {
  const { logout } = useAuth();
  const [emailEditing, setEmailEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submitEmail = async () => {
    if (!newEmail || !currentPassword) { toast.error("Renseignez les deux champs"); return; }
    setBusy(true);
    try {
      await api.changeEmail?.(currentPassword, newEmail);
      toast.success("Email modifié");
      setEmailEditing(false);
      setNewEmail(""); setCurrentPassword("");
    } catch (e) { toast.error(e.message || "Erreur"); }
    finally { setBusy(false); }
  };

  const requestPasswordReset = async () => {
    try {
      await api.forgotPassword?.(user?.email);
      toast.success("Un email de réinitialisation a été envoyé.");
    } catch (e) { toast.error(e.message || "Erreur"); }
  };

  return (
    <div>
      <Row label="Email de connexion" hint="Utilisé pour vous connecter à WYVIO." testId="row-account-email">
        {!emailEditing ? (
          <div className="flex items-center justify-between gap-3">
            <span className="text-[14px] text-black">{user?.email}</span>
            <button
              onClick={() => setEmailEditing(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[12px] font-medium text-black/70"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }}
              data-testid="account-email-edit"
            >
              <Pencil size={12} /> Modifier
            </button>
          </div>
        ) : (
          <div className="space-y-2.5">
            <input
              type="email"
              placeholder="Nouvel email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="w-full rounded-lg bg-white px-3 py-2 text-[14px] outline-none"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }}
              data-testid="account-new-email"
            />
            <input
              type="password"
              placeholder="Mot de passe actuel"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full rounded-lg bg-white px-3 py-2 text-[14px] outline-none"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }}
              data-testid="account-current-pwd"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setEmailEditing(false); setNewEmail(""); setCurrentPassword(""); }}
                className="rounded-full px-3 py-1.5 text-[12px] text-black/55"
              >
                Annuler
              </button>
              <button
                onClick={submitEmail}
                disabled={busy}
                className="rounded-full bg-black px-4 py-1.5 text-[12px] font-medium text-white disabled:opacity-60"
                data-testid="account-email-save"
              >
                {busy ? "…" : "Valider"}
              </button>
            </div>
          </div>
        )}
      </Row>

      <Row label="Mot de passe" hint="Recevez un email pour définir un nouveau mot de passe." testId="row-account-password">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[14px] text-black/55">••••••••</span>
          <button
            onClick={requestPasswordReset}
            className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[12px] font-medium text-black/70"
            style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }}
            data-testid="account-password-reset"
          >
            <Mail size={12} /> Envoyer le lien de réinitialisation
          </button>
        </div>
      </Row>

      <Row label="Session" hint="Terminer la session sur cet appareil." testId="row-account-logout">
        <div className="flex justify-end">
          <button
            onClick={logout}
            className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[12px] font-medium text-red-600/85 transition-colors hover:text-red-600"
            style={{ boxShadow: "inset 0 0 0 1px rgba(239,68,68,0.18)" }}
            data-testid="account-logout"
          >
            <LogOut size={12} /> Se déconnecter
          </button>
        </div>
      </Row>
    </div>
  );
};

/* ═════════════════════════ MAIN PAGE ═══════════════════════ */
export default function SettingsV3() {
  const navigate = useNavigate();
  const { user, agency, refreshAgency } = useAuth();
  const [tab, setTab] = useState("agence");
  const [ag, setAg] = useState(agency);

  useEffect(() => {
    // Rafraîchit à l'ouverture pour partir des données à jour
    api.getAgency().then((a) => { setAg(a); }).catch(() => {});
  }, []);

  const onUpdateAgency = (fresh) => {
    setAg(fresh);
    refreshAgency && refreshAgency();
  };

  const Section = useMemo(() => {
    switch (tab) {
      case "agence":         return <AgencySection agency={ag} onUpdate={onUpdateAgency} />;
      case "equipe":         return <TeamSection />;
      case "notifications":  return <NotificationsSection agency={ag} onUpdate={onUpdateAgency} />;
      case "client":         return <ClientSection agency={ag} onUpdate={onUpdateAgency} />;
      case "compte":         return <AccountSection user={user} />;
      default: return null;
    }
  }, [tab, ag, user]);

  return (
    <div
      className="relative min-h-screen text-black"
      style={{
        background:
          "radial-gradient(1000px 600px at 100% 0%, rgba(200,255,47,0.04) 0%, rgba(200,255,47,0) 45%), linear-gradient(180deg, #FAFAF7 0%, #F5F4EF 100%)",
      }}
      data-testid="settings-v3-page"
    >
      <AppSidebar agency={ag} user={user} testId="settings-v3-sidebar" />

      <main className="lg:pl-[260px]">
        <div className="mx-auto max-w-[1040px] px-10 py-14">
          {/* Header */}
          <div className="text-[11px] uppercase tracking-[0.16em] text-black/45">Paramètres</div>
          <h1 className="mt-3 text-[38px] font-semibold leading-[1.05] tracking-[-0.02em] text-black">
            Réglages
          </h1>
          <p className="mt-2 text-[14.5px] text-black/50">
            Gérez votre agence et vos préférences
          </p>

          {/* Sub-nav */}
          <div className="mt-10">
            <SubNav active={tab} onChange={setTab} />
          </div>

          {/* Section content */}
          <div className="mt-4" data-testid={`settings-content-${tab}`}>
            {Section}
          </div>

          <div className="h-24" />
        </div>
      </main>
    </div>
  );
}
